import React, { useMemo, useState, useCallback } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';
import PaginationComponent from '../../common/components/PaginationComponent';
import ColumnSelectionModal from './components/ColumnSelectionModal';
import { notify } from '../../common/functions/react-toastify.function';
import { bulkDeleteFiberPopSites } from './functions/bulk_delete_fiber_pop_sites.function';
import FiberPopDetailsModal from './FiberPopDetailsModal';
import Swal from 'sweetalert2';

const FiberPopListTable = ({
  fiberPopData,
  selectedFiberPop,
  onAddSinglePop,
  onBulkUpload,
  onBulkDelete,
  onBulkEdit,
  onDeleteSuccess,
  onEditPop,
}) => {
  const [localTableSearchQuery, setLocalTableSearchQuery] = useState('');
  const [isTableSearching, setIsTableSearching] = useState(false);
  const [tableSearchResults, setTableSearchResults] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Upload button state
  const [uploadAnchorEl, setUploadAnchorEl] = useState(null);
  const uploadMenuOpen = Boolean(uploadAnchorEl);

  // Download button state
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  // Action dropdown state
  const [actionValue, setActionValue] = useState('');

  // Column selection modal state
  const [openColumnSelectionModal, setOpenColumnSelectionModal] = useState(false);

  // Row selection and row action menu state
  const [rowSelection, setRowSelection] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);

  // View Details Modal State
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedPopForDetails, setSelectedPopForDetails] = useState(null);

  const handleOpenMenu = (event, rowData) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowForMenu(rowData);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRowForMenu(null);
  };

  const handleViewPop = () => {
    setSelectedPopForDetails(selectedRowForMenu);
    setOpenDetailsModal(true);
    handleCloseMenu();
  };

  const handleEditPop = () => {
    if (!selectedRowForMenu?.id) {
      notify('error', 'Unable to edit: invalid POP data.');
      handleCloseMenu();
      return;
    }
    if (onEditPop) {
      onEditPop(selectedRowForMenu);
    }
    handleCloseMenu();
  };

  const handleDeletePop = async () => {
    if (!selectedRowForMenu || !selectedRowForMenu.id) {
      notify('error', 'Unable to delete: invalid POP data.');
      handleCloseMenu();
      return;
    }

    const popName = selectedRowForMenu.fiber_pop || 'this Fiber POP';

    handleCloseMenu();

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete ${popName}? This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await bulkDeleteFiberPopSites([selectedRowForMenu.id]);
        notify('success', 'Fiber POP deleted successfully');

        // Call parent callback to update data without refetching
        if (onDeleteSuccess) {
          onDeleteSuccess(selectedRowForMenu.id);
        }
      } catch (e) {
        notify('error', 'Failed to delete Fiber POP. Please try again.');
      }
    }
  };

  const handleUploadClick = (event) => {
    setUploadAnchorEl(event.currentTarget);
  };

  const handleUploadClose = () => {
    setUploadAnchorEl(null);
  };

  const handleAddSinglePopClick = () => {
    if (onAddSinglePop) {
      onAddSinglePop();
    }
    handleUploadClose();
  };

  const handleBulkUpload = () => {
    if (onBulkUpload) {
      onBulkUpload();
    }
    handleUploadClose();
  };

  const handleBulkDelete = () => {
    if (onBulkDelete) {
      onBulkDelete();
    }
    handleUploadClose();
  };

  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleDownloadAll = () => {
    const dataToDownload = fiberPopData || [];
    downloadExcelSheetFunction(dataToDownload, 'fiberPopList');
    handleDownloadClose();
  };

  const handleDownloadColumnWise = () => {
    handleDownloadClose();
    setOpenColumnSelectionModal(true);
  };

  const handleDownloadSelectedColumns = (selectedColumnKeys) => {
    // Use all fiber pop data
    const dataToDownload = fiberPopData || [];

    if (dataToDownload.length === 0) {
      notify('warning', 'No data found to download.');
      return;
    }

    // Create a mapping of accessorKey to header for better column names in Excel
    const columnMap = {};
    columns.forEach((col) => {
      columnMap[col.accessorKey] = col.header;
    });

    // Prepare data with only selected columns
    const filteredData = dataToDownload.map((item) => {
      const filteredItem = {};
      selectedColumnKeys.forEach((key) => {
        filteredItem[columnMap[key] || key] = item[key] || '';
      });
      return filteredItem;
    });

    // Download using the existing function
    downloadExcelSheetFunction(filteredData, 'fiberPopList');
    notify('success', `Downloaded ${selectedColumnKeys.length} columns successfully.`);
  };

  // Handle table search input changes
  const handleTableSearchChange = useCallback((event) => {
    const value = event.target.value;
    setLocalTableSearchQuery(value);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  // Handle table search clear
  const handleTableSearchClear = useCallback(() => {
    setLocalTableSearchQuery('');
    setIsTableSearching(false);
    setTableSearchResults(null);
    setCurrentPage(1);
  }, []);

  // Table search effect - searches data locally
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (localTableSearchQuery.trim().length > 1) {
        setIsTableSearching(true);
        // Search in fiberPopData locally
        const searchData = fiberPopData || [];
        const filteredResults = searchData.filter((pop) => {
          const searchTerm = localTableSearchQuery.toLowerCase();
          // Helper function to safely check if a field matches search term
          const matchesField = (value) => {
            if (!value || value === '--' || value === '-') {
              return false;
            }
            return String(value).toLowerCase().includes(searchTerm);
          };

          return (
            matchesField(pop.fiber_pop) ||
            matchesField(pop.latest_status) ||
            matchesField(pop.ran_region) ||
            matchesField(pop.foo_region) ||
            matchesField(pop.city_name) ||
            matchesField(pop.type) ||
            matchesField(pop.telecom_region) ||
            matchesField(pop.site_name) ||
            matchesField(pop.technology) ||
            matchesField(pop.vendor) ||
            matchesField(pop.route)
          );
        });
        setTableSearchResults(filteredResults);
      } else if (localTableSearchQuery.trim().length <= 1) {
        setIsTableSearching(false);
        setTableSearchResults(null);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [localTableSearchQuery, fiberPopData]);

  // Use filtered data when table is searching, otherwise use the original data
  const filteredData = isTableSearching ? tableSearchResults || [] : fiberPopData || [];

  // Calculate pagination
  const totalCount = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Ensure currentPage is within valid range
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    } else if (currentPage < 1 && totalCount > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, totalCount]);

  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  // Get paginated data
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const tableData = filteredData.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    const parsedSize = Number(newPageSize);
    if (Number.isFinite(parsedSize) && parsedSize > 0) {
      setPageSize(parsedSize);
    } else {
      setPageSize(25);
    }
    setCurrentPage(1);
  }, []);

  // Function to get color for ran_region
  const getRanRegionColor = useCallback((ranRegion) => {
    // Handle missing/null/undefined/empty/placeholder values
    if (!ranRegion || ranRegion === '--' || ranRegion === '-' || String(ranRegion).trim() === '') {
      return { bg: '#fff', hover: '#f5f5f5' };
    }

    const region = String(ranRegion).trim();

    // Color mapping for different RAN regions - exact match with data values
    const colorMap = {
      North: { bg: '#FFF3E0', hover: '#FFE0B2' }, // Light orange
      'Central A': { bg: '#E8F5E9', hover: '#C8E6C9' }, // Light green
      'Central B': { bg: '#E3F2FD', hover: '#BBDEFB' }, // Light blue
      South: { bg: '#F3E5F5', hover: '#E1BEE7' }, // Light purple
    };

    // Try exact match first (case-sensitive)
    if (colorMap[region]) {
      return colorMap[region];
    }

    // Try case-insensitive match
    const regionLower = region.toLowerCase();
    for (const [key, colors] of Object.entries(colorMap)) {
      if (key.toLowerCase() === regionLower) {
        return colors;
      }
    }

    // Try partial match (case-insensitive)
    for (const [key, colors] of Object.entries(colorMap)) {
      const keyLower = key.toLowerCase();
      if (regionLower.includes(keyLower) || keyLower.includes(regionLower)) {
        return colors;
      }
    }

    // Default: hash-based color for unknown regions
    let hash = 0;
    for (let i = 0; i < region.length; i++) {
      hash = region.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    return {
      bg: `hsla(${hue}, 70%, 95%, 1)`,
      hover: `hsla(${hue}, 70%, 85%, 1)`,
    };
  }, []);

  const handleActionChange = async (value) => {
    if (!value) {
      return;
    }

    // Bulk Delete (from Excel file) – no row selection required
    if (value === 'bulkDeleteFromFile') {
      if (onBulkDelete) {
        onBulkDelete();
      }
      setActionValue('');
      return;
    }

    // Bulk Edit (from Excel file) – no row selection required
    if (value === 'bulkEdit') {
      if (onBulkEdit) {
        onBulkEdit();
      }
      setActionValue('');
      return;
    }

    // Get the selected row indices/keys
    const selectedRowIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);

    if (!selectedRowIds.length) {
      notify('warning', 'Please select at least one POP');
      setActionValue('');
      return;
    }

    // Get the actual data IDs from the selected rows
    const selectedIds = tableData
      .filter((row) => {
        const rowId = row.id ? row.id.toString() : `pop_${row.fiber_pop}`;
        return selectedRowIds.includes(rowId);
      })
      .map((row) => row.id)
      .filter((id) => id !== null && id !== undefined);

    if (!selectedIds.length) {
      notify('warning', 'Unable to get IDs for selected POPs');
      setActionValue('');
      return;
    }

    if (value === 'bulkDelete') {
      // Show confirmation dialog
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `Do you want to delete ${selectedIds.length} selected Fiber POP(s)? This action cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete them!',
        cancelButtonText: 'Cancel',
      });

      if (!result.isConfirmed) {
        setActionValue('');
        return;
      }

      try {
        await bulkDeleteFiberPopSites(selectedIds);
        notify('success', `${selectedIds.length} Fiber POP(s) deleted successfully`);

        // Update local state by filtering out deleted items
        if (onDeleteSuccess) {
          onDeleteSuccess(selectedIds); // Pass the entire array of IDs
        }

        // Clear selection
        setRowSelection({});
      } catch (error) {
        notify('error', 'Failed to delete some Fiber POPs. Please try again.');
      } finally {
        setActionValue('');
      }
    }
  };

  const columns = useMemo(
    () =>
      [
        {
          accessorKey: 'fiber_pop',
          header: 'Fiber POP',
          size: 120,
          muiTableHeadCellProps: {
            style: { paddingLeft: '16px' },
          },
          Cell: ({ cell }) => <div style={{ paddingLeft: '16px' }}>{cell.getValue() || '--'}</div>,
        },
        {
          accessorKey: 'latest_status',
          header: 'Status',
          size: 120,
        },
        {
          accessorKey: 'ran_region',
          header: 'RAN Region',
          size: 120,
        },
        {
          accessorKey: 'foo_region',
          header: 'FOO Region',
          size: 100,
        },
        {
          accessorKey: 'city_name',
          header: 'City',
          size: 120,
        },
        {
          accessorKey: 'type',
          header: 'Type',
          size: 80,
        },
        {
          accessorKey: 'telecom_region',
          header: 'Telecom Region',
          size: 140,
        },
        {
          accessorKey: 'ran_site',
          header: 'RAN Site',
          size: 100,
        },
        {
          accessorKey: 'site_name',
          header: 'Site Name',
          size: 200,
        },
        {
          accessorKey: 'technology',
          header: 'Technology',
          size: 120,
        },
        {
          accessorKey: 'vendor',
          header: 'Vendor',
          size: 100,
        },
        {
          accessorKey: 'route',
          header: 'Route',
          size: 80,
        },
        {
          accessorKey: 'latt',
          header: 'Latitude',
          size: 120,
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value !== null && value !== undefined ? parseFloat(value).toFixed(6) : '--';
          },
        },
        {
          accessorKey: 'long',
          header: 'Longitude',
          size: 120,
          Cell: ({ cell }) => {
            const value = cell.getValue();
            return value !== null && value !== undefined ? parseFloat(value).toFixed(6) : '--';
          },
        },
      ].map((column) => ({
        ...column,
        muiTableHeadCellProps: {
          align: 'center',
          sx: {
            color: '#344054',
            backgroundColor: '#E8E9E9',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '600',
            lineHeight: '125%',
            height: '32px',
            padding: '4px 8px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        muiTableBodyCellProps: {
          align: 'center',
          sx: {
            color: '#667085',
            backgroundColor: 'transparent !important',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '125%',
            height: '48px',
            padding: '8px 12px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      })),
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    getRowId: (row) => {
      if (row.id) {
        return row.id.toString();
      }
      if (row.fiber_pop) {
        return `pop_${row.fiber_pop}`;
      }
      return `row_${Math.random().toString(36).substr(2, 9)}`;
    },
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableColumnVisibility: true,
    enableDensityToggle: true,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableSorting: true,
    enableFilters: true,
    enableGlobalFilter: false,
    enableColumnResizing: false,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableSelectAll: true,
    enablePagination: false,
    enableBottomToolbar: false,
    enableStickyFooter: false,
    enableStickyHeader: true,
    positionToolbarAlertBanner: 'none', // Hide the built-in selection toolbar
    initialState: {
      density: 'comfortable',
    },
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    enableRowActions: true,
    renderRowActions: ({ row }) => (
      <IconButton
        onClick={(e) => handleOpenMenu(e, row.original)}
        size="small"
        sx={{
          padding: '2px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    ),
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '', // Hide the header text for actions column
      },
    },

    muiTableContainerProps: {
      sx: {
        maxHeight: 'calc(100vh - 275px)',
        overflowX: 'auto',
        width: '100%',
        '& .MuiTable-root': {
          width: '100%',
          tableLayout: 'auto', // Changed from 'fixed' to 'auto' for better action column positioning
        },
        '& .MuiTableCell-root': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '& .MuiTableRow-root .MuiTableCell-root:first-child': {
          width: '50px',
          minWidth: '50px',
          maxWidth: '50px',
          padding: '4px 8px',
          textAlign: 'left',
        },
        '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:first-child': {
          width: '50px',
          minWidth: '50px',
          maxWidth: '50px',
          padding: '8px 8px',
          textAlign: 'left',
          backgroundColor: '#E8E9E9',
          color: '#344054',
          fontSize: '12px',
          fontWeight: '600',
          height: '32px',
        },
        '& .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px',
        },
        '& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:last-child': {
          width: '60px',
          minWidth: '60px',
          maxWidth: '60px',
          padding: '4px 8px',
          textAlign: 'center',
          backgroundColor: '#E8E9E9',
          color: '#344054',
          fontSize: '12px',
          fontWeight: '600',
          height: '32px',
        },
        // Ensure sticky header works properly
        '& .MuiTableHead-root': {
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#E8E9E9',
        },
        // Custom checkbox styling
        '& .MuiCheckbox-root': {
          padding: '2px',
          '& .MuiSvgIcon-root': {
            fontSize: '18px',
          },
        },
        '& .MuiCheckbox-root.Mui-checked': {
          color: '#BD1D23',
          '& .MuiSvgIcon-root': {
            fontSize: '18px',
          },
        },
        '& .MuiCheckbox-root.MuiCheckbox-indeterminate': {
          color: '#BD1D23',
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: 'transparent', // Changed from '#fff' to 'transparent' to inherit row color
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '125%',
        height: '20px', // Further reduced row height for compact density
        padding: '1px 4px', // Further reduced padding for compact density
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        width: '100%',
        overflow: 'hidden',
        border: '1px solid #dad9d9',

        '& .MuiTableContainer-root': {
          overflowX: 'auto',
          maxWidth: '100%',
          height: '90%',
          maxHeight: '71vh',
        },
        '& .MuiTable-root': {
          width: '100%',
          tableLayout: 'auto',
        },
        // Ensure row colors are applied - override MaterialReactTable defaults
        '& .MuiTableBody-root .MuiTableRow-root': {
          '& .MuiTableCell-root': {
            backgroundColor: 'transparent !important',
          },
        },
        '& tbody tr': {
          '& td': {
            backgroundColor: 'transparent !important',
          },
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const ranRegion = row.original.ran_region;
      const regionColors = getRanRegionColor(ranRegion);
      const isSelected =
        selectedFiberPop &&
        row.original.fiber_pop?.toLowerCase() === selectedFiberPop.toLowerCase();

      // Debug: uncomment to see what colors are being applied
      // console.log('Row:', row.original.fiber_pop, 'Region:', ranRegion, 'Colors:', regionColors);

      return {
        sx: {
          backgroundColor: isSelected ? '#FFF4E6 !important' : `${regionColors.bg} !important`,
          borderBottom: '1px solid #E5E7EB',
          '&:hover': {
            backgroundColor: isSelected ? '#FFE8CC !important' : `${regionColors.hover} !important`,
          },
          '& .MuiTableCell-root': {
            backgroundColor: 'transparent !important',
            borderColor: 'transparent',
            borderBottom: '1px solid #E5E7EB',
          },
          '& td': {
            backgroundColor: 'transparent !important',
            borderBottom: '1px solid #E5E7EB',
          },
        },
      };
    },
    muiTableRowProps: ({ row }) => {
      const ranRegion = row.original.ran_region;
      const regionColors = getRanRegionColor(ranRegion);
      const isSelected =
        selectedFiberPop &&
        row.original.fiber_pop?.toLowerCase() === selectedFiberPop.toLowerCase();

      return {
        sx: {
          backgroundColor: isSelected ? '#FFF4E6 !important' : `${regionColors.bg} !important`,
          borderBottom: '1px solid #E5E7EB',
          '&:hover': {
            backgroundColor: isSelected ? '#FFE8CC !important' : `${regionColors.hover} !important`,
          },
          '& .MuiTableCell-root': {
            backgroundColor: 'transparent !important',
            borderColor: 'transparent',
            borderBottom: '1px solid #E5E7EB',
          },
        },
      };
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '50%',
          flexWrap: 'nowrap',
          minWidth: 0,
          gap: 8,
        }}
      >
        {/* Table Search Input - Local Search */}
        <TextField
          size="small"
          placeholder="Search in table..."
          value={localTableSearchQuery}
          onChange={handleTableSearchChange}
          variant="outlined"
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              height: '32px',
              fontSize: '12px',
              '& fieldset': {
                borderColor: '#E0E0E0',
              },
              '&:hover fieldset': {
                borderColor: '#BD1D23',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#BD1D23',
              },
            },
            '& .MuiInputBase-input': {
              padding: '6px 12px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: '#666' }} />
              </InputAdornment>
            ),
            endAdornment: localTableSearchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleTableSearchClear} sx={{ padding: '2px' }}>
                  <ClearIcon sx={{ fontSize: 16, color: '#666' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Upload Button */}
        <Button
          variant="contained"
          size="small"
          style={{
            background: '#BD1D23',
            color: 'white',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '6px 16px',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(245, 248, 247, 0.3)',
            border: 'none',
            textTransform: 'none',
            height: '32px',
          }}
          endIcon={<ArrowDropDownIcon style={{ fontSize: 16, color: 'white' }} />}
          onClick={handleUploadClick}
        >
          Upload
        </Button>

        {/* Download Button with Dropdown */}
        <Button
          variant="contained"
          size="small"
          style={{
            background: '#f8e8e9',
            color: '#333',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '6px 16px',
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
            border: 'none',
            textTransform: 'none',
          }}
          onClick={handleDownloadClick}
          endIcon={<ArrowDropDownIcon style={{ fontSize: 16, color: '#333' }} />}
        >
          <DownloadIcon style={{ fontSize: 16, marginRight: 4, color: '#333' }} />
          Download
        </Button>

        {/* Action Dropdown */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: '12px', color: '#333' }}>Action</InputLabel>
          <Select
            value={actionValue}
            onChange={(e) => handleActionChange(e.target.value)}
            label="Action"
            sx={{
              fontSize: '12px',
              height: '32px',
              background: '#f8e8e9',
              boxShadow: '0 2px 8px rgba(168, 237, 234, 0.3)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '& .MuiSelect-icon': {
                color: '#333',
              },
              '& .MuiSelect-select': {
                padding: '6px 12px',
                lineHeight: 1.2,
                color: '#333',
                fontWeight: 600,
              },
              '& .MuiInputLabel-root': {
                color: '#333',
                fontSize: '12px',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#333',
              },
              '& .MuiInputLabel-shrink': {
                color: '#333',
                fontSize: '11px',
              },
            }}
          >
            <MenuItem value="bulkEdit" sx={{ fontSize: '12px' }}>
              <EditOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
              Bulk Edit
            </MenuItem>
            <MenuItem value="bulkDelete" sx={{ fontSize: '12px' }}>
              <DeleteOutlineIcon sx={{ mr: 1, fontSize: 16, color: '#d32f2f' }} />
              Bulk Delete (selected rows)
            </MenuItem>
            <MenuItem value="bulkDeleteFromFile" sx={{ fontSize: '12px' }}>
              <DeleteOutlineIcon sx={{ mr: 1, fontSize: 16, color: '#d32f2f' }} />
              Bulk Delete (from file)
            </MenuItem>
          </Select>
        </FormControl>

        {/* Selection Count Indicator */}
        {Object.keys(rowSelection).filter((k) => rowSelection[k]).length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: '12px',
              color: '#667085',
              fontWeight: 500,
            }}
          >
            <span>
              {Object.keys(rowSelection).filter((k) => rowSelection[k]).length} of {totalCount}{' '}
              row(s) selected
            </span>
            <Button
              size="small"
              onClick={() => setRowSelection({})}
              sx={{
                fontSize: '11px',
                textTransform: 'none',
                minWidth: 'auto',
                padding: '2px 8px',
                color: '#BD1D23',
                '&:hover': {
                  backgroundColor: 'rgba(189, 29, 35, 0.08)',
                },
              }}
            >
              Clear
            </Button>
          </Box>
        )}

        <div style={{ flex: 1, minWidth: 0 }} />
      </div>
    ),
  });

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        padding: '16px',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: '0 0 90%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <MaterialReactTable table={table} />
      </Box>

      {/* Pagination */}
      {totalCount > 0 && (
        <Box sx={{ flex: '0 0 auto', mt: 2, mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <PaginationComponent
            currentPage={safePage}
            totalPages={totalPages}
            totalCount={totalCount}
            limit={pageSize}
            onPageChange={handlePageChange}
            onLimitChange={handlePageSizeChange}
          />
        </Box>
      )}

      {/* Row actions menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleViewPop}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
          View
        </MenuItem>
        <MenuItem onClick={handleEditPop}>
          <EditOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeletePop}>
          <DeleteOutlineIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Upload Menu */}
      <Menu
        anchorEl={uploadAnchorEl}
        open={uploadMenuOpen}
        onClose={handleUploadClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleAddSinglePopClick}>
          <EditOutlinedIcon sx={{ mr: 1, fontSize: 18 }} />
          Add Single POP
        </MenuItem>
        <MenuItem onClick={handleBulkUpload}>
          <UploadFileIcon sx={{ mr: 1, fontSize: 18 }} />
          Bulk Upload
        </MenuItem>
      </Menu>

      {/* Download Menu */}
      <Menu
        anchorEl={downloadAnchorEl}
        open={downloadMenuOpen}
        onClose={handleDownloadClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleDownloadAll}>Download All</MenuItem>
        <MenuItem onClick={handleDownloadColumnWise}>Download Column Wise</MenuItem>
      </Menu>

      {/* Column Selection Modal */}
      <ColumnSelectionModal
        open={openColumnSelectionModal}
        onClose={() => setOpenColumnSelectionModal(false)}
        columns={columns}
        onDownload={handleDownloadSelectedColumns}
        data={fiberPopData}
      />

      {/* Fiber POP Details Modal */}
      <FiberPopDetailsModal
        open={openDetailsModal}
        onClose={() => setOpenDetailsModal(false)}
        popData={selectedPopForDetails}
      />
    </Box>
  );
};

export default FiberPopListTable;
